import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { toast } from 'sonner';
import { 
  Building2, Plus, Search, CheckCircle, BookOpen, FileText, 
  Clock, MoreVertical, Edit, Trash2, Loader2, ExternalLink 
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPublishers = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPublisher, setEditingPublisher] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchPublishers();
  }, []);

  const fetchPublishers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers?limit=100&search=${encodeURIComponent(search)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPublishers(data.publishers || []);
      }
    } catch (err) {
      console.error('Error fetching publishers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (publisherId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/publishers/${publisherId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to delete');
      }
      
      toast.success('Publisher deleted');
      setDeleteConfirm(null);
      fetchPublishers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 p-6" data-testid="admin-publishers-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Building2 className="w-6 h-6 text-purple-500" />
              Publishers
            </h1>
            <p className="text-gray-400 text-sm">Manage publisher entities</p>
          </div>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="create-publisher-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Publisher
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input
            placeholder="Search publishers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchPublishers()}
            className="pl-10 bg-gray-900 border-gray-800"
          />
        </div>

        {/* Publishers List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : publishers.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 rounded-lg border border-gray-800">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">No publishers found</p>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
            <table className="w-full" data-testid="publishers-table">
              <thead className="bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Publisher</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">Comics</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">Novels</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-400">Pending</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {publishers.map((publisher) => (
                  <tr key={publisher.id} className="hover:bg-gray-800/30" data-testid={`publisher-row-${publisher.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-800 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
                          {publisher.logo_url ? (
                            <img src={publisher.logo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Building2 className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{publisher.name}</span>
                            {publisher.verified && (
                              <CheckCircle className="w-4 h-4 text-purple-500" />
                            )}
                          </div>
                          <span className="text-xs text-gray-500">/{publisher.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {publisher.verified ? (
                        <span className="text-green-400 text-sm">Verified</span>
                      ) : (
                        <span className="text-gray-500 text-sm">Unverified</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="flex items-center justify-center gap-1 text-gray-300">
                        <BookOpen className="w-4 h-4 text-gray-500" />
                        {publisher.series_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="flex items-center justify-center gap-1 text-gray-300">
                        <FileText className="w-4 h-4 text-gray-500" />
                        {publisher.novel_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {publisher.pending_count > 0 ? (
                        <span className="flex items-center justify-center gap-1 text-yellow-400">
                          <Clock className="w-4 h-4" />
                          {publisher.pending_count}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-gray-900 border-gray-800">
                          <DropdownMenuItem
                            className="text-gray-300 cursor-pointer"
                            onClick={() => window.open(`/publisher/${publisher.slug}`, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Page
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-gray-300 cursor-pointer"
                            onClick={() => setEditingPublisher(publisher)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-400 cursor-pointer"
                            onClick={() => setDeleteConfirm(publisher)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        <PublisherFormModal
          isOpen={showCreateModal || !!editingPublisher}
          onClose={() => {
            setShowCreateModal(false);
            setEditingPublisher(null);
          }}
          publisher={editingPublisher}
          onSuccess={() => {
            setShowCreateModal(false);
            setEditingPublisher(null);
            fetchPublishers();
          }}
        />

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-gray-900 border-gray-800 text-white">
            <DialogHeader>
              <DialogTitle>Delete Publisher</DialogTitle>
            </DialogHeader>
            <p className="text-gray-400">
              Are you sure you want to delete <strong className="text-white">{deleteConfirm?.name}</strong>?
              This action cannot be undone.
            </p>
            {(deleteConfirm?.series_count > 0 || deleteConfirm?.novel_count > 0) && (
              <p className="text-yellow-400 text-sm bg-yellow-950/30 p-2 rounded">
                This publisher has associated content and cannot be deleted until all associations are removed.
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(deleteConfirm?.id)}
                disabled={deleteConfirm?.series_count > 0 || deleteConfirm?.novel_count > 0}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Publisher Form Modal Component
const PublisherFormModal = ({ isOpen, onClose, publisher, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    website_url: '',
    verified: false
  });

  useEffect(() => {
    if (publisher) {
      setFormData({
        name: publisher.name || '',
        slug: publisher.slug || '',
        description: publisher.description || '',
        logo_url: publisher.logo_url || '',
        website_url: publisher.website_url || '',
        verified: publisher.verified || false
      });
    } else {
      setFormData({
        name: '',
        slug: '',
        description: '',
        logo_url: '',
        website_url: '',
        verified: false
      });
    }
  }, [publisher]);

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[-\s]+/g, '-')
      .slice(0, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const url = publisher
        ? `${API_URL}/api/admin/publishers/${publisher.id}`
        : `${API_URL}/api/admin/publishers`;
      
      const res = await fetch(url, {
        method: publisher ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          slug: formData.slug || generateSlug(formData.name)
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to save');
      }

      toast.success(publisher ? 'Publisher updated' : 'Publisher created');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {publisher ? 'Edit Publisher' : 'Create Publisher'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  name: e.target.value,
                  slug: formData.slug || generateSlug(e.target.value)
                })}
                className="bg-gray-800 border-gray-700"
                data-testid="publisher-form-name"
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Slug</label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="auto-generated"
                className="bg-gray-800 border-gray-700"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-gray-800 border-gray-700 min-h-[80px]"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Logo URL</label>
            <Input
              value={formData.logo_url}
              onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
              placeholder="https://..."
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Website URL</label>
            <Input
              value={formData.website_url}
              onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
              placeholder="https://..."
              className="bg-gray-800 border-gray-700"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="verified"
              checked={formData.verified}
              onChange={(e) => setFormData({ ...formData, verified: e.target.checked })}
              className="rounded border-gray-700 bg-gray-800"
            />
            <label htmlFor="verified" className="text-sm text-gray-300">
              Verified Publisher
            </label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-publisher-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (publisher ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPublishers;
